import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { ArrowLeft, Edit, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate } from "../lib/format";
import { FAStatusBadge } from "../components/etams/StatusBadge";
import { apiErrorMessage } from "../lib/inventoryApi";
import { labelPaket } from "../lib/faKonfigurasi";
import { FA_STATUS_LABEL } from "../lib/statusLabels";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";
import { FA_ISI_ROLES, DR_CREATE_ROLES } from "../lib/roles";
import { nomorBermasalah } from "../lib/statusWa";
import { tampilHp } from "../lib/nomorHp";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

//: Cermin `URUT_SAH_RIWAYAT_FA` di backend/routers/customers.py.
const URUT_SAH_RIWAYAT = ["created_at", "fa_number", "deadline_date", "total_amount_idr", "order_status"];

const KOLOM_CSV_RIWAYAT = [
  { kunci: "fa_number", judul: "Nomor FA" },
  { kunci: "package_type", judul: "Paket", ambil: (f) => labelPaket(f.package_type) },
  { kunci: "total_amount_idr", judul: "Total", ambil: (f) => f.total_amount_idr ?? "" },
  { kunci: "order_status", judul: "Status", ambil: (f) => FA_STATUS_LABEL[f.order_status] || f.order_status },
  { kunci: "deadline_date", judul: "Deadline", ambil: (f) => formatDate(f.deadline_date) },
  { kunci: "created_at", judul: "Dibuat", ambil: (f) => formatDate(f.created_at) },
];

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOwner, profile } = useAuth();
  // K206 / VONIS-HAK-BACA-ISI-FA: kontak customer, nilai transaksi, dan riwayat FA-nya hanya Owner/KC/CS. Peran lain
  // yang boleh membuka modul customer (Admin Produksi/Gudang/Purchasing) melihat nama, kota, cabang, dan jumlah FA.
  const isiLengkap = FA_ISI_ROLES.includes(profile?.role?.code);
  const [cust, setCust] = useState(null);
  const [history, setHistory] = useState([]);
  const [totalHistory, setTotalHistory] = useState(0);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Riwayat FA — pola daftar seragam mode SERVER (28 Sep 2026): customer repeat order
  // bertahun-tahun tidak lagi memuat seluruh riwayatnya sekaligus. `fa-history` berhalaman
  // begitu `limit` dikirim (tanpa itu tetap daftar polos untuk pemanggil lama).
  const daftar = useDaftar(history, {
    server: { total: totalHistory, urutSah: URUT_SAH_RIWAYAT },
    urutAwal: { kunci: "created_at", arah: "desc" },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;

  const bangunParams = useCallback((ovr = {}) => ({
    ...(cari && { q: cari }), limit, offset, sort, arah, ...ovr,
  }), [cari, limit, offset, sort, arah]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get(`/customers/${id}/fa-history`, { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [id, bangunParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const c = await api.get(`/customers/${id}`);
      setCust(c.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat data customer."));
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const loadHistory = useCallback(async () => {
    if (!isiLengkap) { setLoadingHistory(false); return; }   // backend menolak 403 — tidak dipanggil
    setLoadingHistory(true);
    try {
      const h = await api.get(`/customers/${id}/fa-history`, { params: bangunParams() });
      setHistory(h.data?.items || []);
      setTotalHistory(typeof h.data?.total === "number" ? h.data.total : 0);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Riwayat FA gagal dimuat."));
    } finally {
      setLoadingHistory(false);
    }
  }, [id, bangunParams, isiLengkap]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleDelete = async () => {
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer dihapus");
      navigate("/customers");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menghapus customer."));
    }
  };

  if (loading || !cust) return <div data-testid="customer-detail-loading" className="text-sm text-muted-foreground">Memuat...</div>;

  const totalFa = cust.summary?.total_fa || 0;
  const totalAmount = cust.summary?.total_amount_idr || 0;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <button onClick={() => navigate("/customers")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-to-customers">
        <ArrowLeft size={14} /> Kembali ke daftar customer
      </button>

      <Card className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="customer-name">{cust.name}</h1>
            {/* KONTRAS-01: induknya SENGAJA nol kelas warna — NILAI-nya
                mewarisi `text-card-foreground`, sementara LABEL-nya di bawah
                tetap `text-muted-foreground`. Kalau induk ikut diberi
                `text-muted-foreground`, label dan nilai jadi warna yang sama
                persis dan hierarki bacanya hilang — dulu dibedakan
                abu-abu muda vs abu-abu tua. */}
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1 text-sm">
              {isiLengkap && <div><span className="text-muted-foreground">Telepon:</span> <span data-testid="customer-phone">{tampilHp(cust.phone) || "—"}</span></div>}
              {/* BUSINESS-100: CS mencatat nomor ini salah / tidak aktif saat WhatsApp penting tidak sampai.
                  Tanda hilang sendiri begitu nomornya dibetulkan. */}
              {isiLengkap && nomorBermasalah(cust) && (
                <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1 text-destructive"
                     data-testid="customer-wa-bermasalah">
                  Nomor WhatsApp bermasalah: {nomorBermasalah(cust).label}
                  {nomorBermasalah(cust).pada && ` (dicatat CS ${formatDate(nomorBermasalah(cust).pada)})`}.
                  Pastikan nomor yang benar ke customer, lalu perbarui teleponnya.
                </div>
              )}
              {isiLengkap && <div><span className="text-muted-foreground">Email:</span> <span data-testid="customer-email">{cust.email || "—"}</span></div>}
              <div><span className="text-muted-foreground">Kota:</span> {cust.city || "—"}</div>
              <div><span className="text-muted-foreground">Wilayah/Cabang asal:</span> {cust.region?.name || "—"} · {cust.store?.name || "—"}</div>
              {cust.address && <div className="sm:col-span-2"><span className="text-muted-foreground">Alamat:</span> {cust.address}</div>}
              {cust.notes && <div className="sm:col-span-2"><span className="text-muted-foreground">Catatan:</span> {cust.notes}</div>}
            </div>
          </div>
          <div className="flex gap-2">
            {isiLengkap && <Button variant="outline" onClick={() => setEditing(true)} data-testid="customer-edit-btn"><Edit size={14} className="mr-1" />Edit</Button>}
            {isOwner && (
              <Button variant="destructive" disabled={totalFa > 0} onClick={() => setConfirmDelete(true)} data-testid="customer-delete-btn" title={totalFa > 0 ? "Tidak dapat dihapus: ada FA terkait" : ""}>
                <Trash2 size={14} className="mr-1" /> Hapus
              </Button>
            )}
          </div>
        </div>
        <div className={`mt-4 grid ${isiLengkap ? "grid-cols-3" : "grid-cols-2"} gap-4 text-sm`}>
          <div className="bg-muted/50 p-3 rounded">
            <div className="text-xs uppercase text-muted-foreground">Total FA</div>
            <div className="text-lg font-semibold mt-1" data-testid="customer-total-fa">{totalFa}</div>
          </div>
          {isiLengkap && (
            <div className="bg-muted/50 p-3 rounded">
              <div className="text-xs uppercase text-muted-foreground">Total Transaksi</div>
              <div className="text-lg font-semibold mt-1" data-testid="customer-total-amount">{formatIDR(totalAmount)}</div>
            </div>
          )}
          <div className="bg-muted/50 p-3 rounded">
            <div className="text-xs uppercase text-muted-foreground">FA Terakhir</div>
            <div className="text-sm font-medium mt-1">{formatDate(cust.summary?.last_fa_at)}</div>
          </div>
        </div>
      </Card>

      {!isiLengkap ? (
        <Card className="p-6 text-sm text-muted-foreground" data-testid="fa-history-bukan-hak">
          Riwayat FA customer (isi pesanan dan nilainya) hanya bisa dibuka Owner, Kepala Cabang, dan CS.
        </Card>
      ) : (
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold">Riwayat FA</h2>
          {/* FA lahir dari PD (K35/K55) — rute `/fa/new` nol pernah ada
              (BUAT-FA-BARU-DARI-CUSTOMER-RUTE-MATI-01). Pintunya = PD baru
              dengan customer ini sudah terpilih. */}
          {DR_CREATE_ROLES.includes(profile?.role?.code) && (
            <Button onClick={() => navigate(`/design-requests/new?customer_id=${id}`)} data-testid="new-fa-for-customer">
              <Plus size={14} className="mr-1" /> Buat PD Baru
            </Button>
          )}
        </div>
        {!loadingHistory && totalHistory === 0 && !daftar.cari.trim() ? (
          <div className="text-sm text-muted-foreground py-6 text-center">Belum ada FA untuk customer ini.</div>
        ) : (
          <>
            <PalangDaftar daftar={daftar} placeholder="Cari nomor FA" testId="fa-history-palang"
                          ekspor={{ nama: `riwayat_fa_${(cust.name || "customer").replace(/\s+/g, "_")}`, kolom: KOLOM_CSV_RIWAYAT, ambilHalaman }} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                  <tr className="text-left">
                    <ThUrut daftar={daftar} kunci="fa_number" className="px-3 py-2">Nomor FA</ThUrut>
                    <th className="px-3 py-2">Paket</th>
                    <ThUrut daftar={daftar} kunci="total_amount_idr" className="px-3 py-2">Total</ThUrut>
                    <ThUrut daftar={daftar} kunci="order_status" className="px-3 py-2">Status</ThUrut>
                    <ThUrut daftar={daftar} kunci="deadline_date" className="px-3 py-2">Deadline</ThUrut>
                    <ThUrut daftar={daftar} kunci="created_at" className="px-3 py-2">Dibuat</ThUrut>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory && daftar.tampil.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">Memuat...</td></tr>
                  )}
                  {!loadingHistory && daftar.tampil.length === 0 && (
                    <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground" data-testid="fa-history-kosong">
                      Tidak ada FA yang cocok dengan pencarian ini.
                    </td></tr>
                  )}
                  {daftar.tampil.map((fa) => (
                    <tr key={fa.id} className="border-t border-border hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/fa-orders/${fa.id}`)} data-testid={`fa-history-row-${fa.id}`}>
                      {/* KONTRAS-01: nol kelas warna, SENGAJA — warisi
                          `text-card-foreground` dari <Card> (pola c96562f). */}
                      <td className="px-3 py-2 font-medium">{fa.fa_number}</td>
                      <td className="px-3 py-2 text-muted-foreground">{labelPaket(fa.package_type)}</td>
                      <td className="px-3 py-2">{formatIDR(fa.total_amount_idr)}</td>
                      {/* CUST-02: `payment_status` dihapus migration 004 —
                          `/customers/{id}/fa-history` sekarang mengirim
                          `order_status`, dan FAStatusBadge memetakan 12 nilai
                          CHECK-nya. */}
                      <td className="px-3 py-2"><FAStatusBadge value={fa.order_status} /></td>
                      <td className="px-3 py-2">{formatDate(fa.deadline_date)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{formatDate(fa.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Paginasi daftar={daftar} testId="fa-history-paginasi" />
          </>
        )}
      </Card>
      )}

      {editing && (
        <EditCustomerModal
          customer={cust}
          role={profile?.role?.code}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="confirm-delete-customer">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Customer?</AlertDialogTitle>
            <AlertDialogDescription>
              Aksi ini permanen. Customer "{cust.name}" akan dihapus dari sistem.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-danger text-danger-foreground hover:bg-danger/90" data-testid="confirm-delete-yes">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function EditCustomerModal({ customer, role, onClose, onSaved }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: customer.name || "", phone: tampilHp(customer.phone), email: customer.email || "",
    address: customer.address || "", city: customer.city || "", notes: customer.notes || "",
    store_origin_id: customer.store_origin_id || "",
  });
  // T1: cabang asal bisa diubah Owner / Kepala Cabang (backend menjepit KC ke wilayahnya; tercatat di audit).
  const bolehPindahCabang = role === "OWNER" || role === "KEPALA_CABANG";
  const [stores, setStores] = useState([]);
  useEffect(() => {
    if (!bolehPindahCabang) return;
    api.get("/stores").then((r) => setStores(Array.isArray(r.data) ? r.data : []))
      .catch((e) => toast.error(apiErrorMessage(e, "Daftar cabang gagal dimuat.")));
  }, [bolehPindahCabang]);
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(), phone: form.phone || null,
        email: form.email ? form.email.trim().toLowerCase() : null,
        address: form.address || null, city: form.city || null, notes: form.notes || null,
      };
      if (bolehPindahCabang && form.store_origin_id && form.store_origin_id !== customer.store_origin_id) {
        body.store_origin_id = form.store_origin_id;
      }
      await api.patch(`/customers/${customer.id}`, body);
      toast.success("Data customer disimpan");
      onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "CUSTOMER_HP_SUDAH_ADA" && d.existing_customer_id) {
        toast.error(d.message, {
          duration: 12000,
          action: { label: "Buka data customer", onClick: () => navigate(`/customers/${d.existing_customer_id}`) },
        });
        return;
      }
      toast.error(apiErrorMessage(e, "Gagal menyimpan perubahan customer."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="customer-edit-modal">
        <DialogHeader><DialogTitle>Edit Customer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 py-2">
          <div className="md:col-span-2">
            <Label>Nama *</Label>
            <Input value={form.name} onChange={(e) => upd("name", e.target.value)} data-testid="edit-customer-name" />
          </div>
          <div><Label>Telepon</Label><Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></div>
          <div><Label>Email</Label><Input value={form.email} onChange={(e) => upd("email", e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Alamat</Label><Textarea rows={2} value={form.address} onChange={(e) => upd("address", e.target.value)} /></div>
          <div><Label>Kota</Label><Input value={form.city} onChange={(e) => upd("city", e.target.value)} /></div>
          {bolehPindahCabang && (
            <div>
              <Label>Cabang asal</Label>
              <Select value={form.store_origin_id} onValueChange={(v) => upd("store_origin_id", v)}>
                <SelectTrigger data-testid="edit-customer-store"><SelectValue placeholder="Pilih cabang" /></SelectTrigger>
                <SelectContent>
                  {stores.map((st) => <SelectItem key={st.id} value={st.id}>{st.name}{st.region?.name ? ` · ${st.region.name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="md:col-span-2"><Label>Catatan</Label><Textarea rows={2} value={form.notes} onChange={(e) => upd("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={save} disabled={saving} data-testid="edit-customer-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
